import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQrCodeModal } from "../QrCodeModalContext";
import ReviewButton from "../avaliacao/ReviewButton";
import DeleteReviewModal from "../../myReviews/deleteReviewModal";
import styles from "./card-info.module.css";
import axios from "axios";

interface AvaliacaoUsuario {
  id: number;
  nota: number;
  comentario?: string;
}

interface BookingCardProps {
  compraId: number;
  status: string;
  dataReserva: string;
  precoTotal: number;
  adultos: number;
  criancas: number;
  motoristaNome: string;
  pacote: {
    id: number;
    titulo: string;
    duracaoEmMinutos: number;
    primeiraFotoId?: number;
  };
  qrCodeUsuario?: string;
  unreadCount?: number;
  avaliacao?: AvaliacaoUsuario; // nova prop
}

const BookingCard: React.FC<BookingCardProps> = ({
  compraId,
  status,
  dataReserva,
  precoTotal,
  adultos,
  criancas,
  motoristaNome,
  pacote,
  qrCodeUsuario,
  unreadCount = 0,
  avaliacao,
}) => {
  const BASE_URL = process.env.REACT_APP_API_URL;
  const { openModal } = useQrCodeModal();

  // 🔹 Estado para modal de delete e avaliação atual
  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(null);
  const [currentAvaliacao, setCurrentAvaliacao] = useState(avaliacao);

  const imageUrl = pacote.primeiraFotoId
    ? `${BASE_URL}/uploads/${pacote.primeiraFotoId}`
    : "/assets/img/no-image.jpg";

  const statusLabels: Record<string, string> = {
    PENDENTE: "Pending",
    PAGO: "Paid",
    CANCELADO: "Cancelled",
    CONCLUIDO: "Completed",
    EM_ANDAMENTO: "In Progress",
  };

  // 🔹 Remove a avaliação atual do estado quando deletada
  const handleDeleteReview = (id: number) => {
    if (currentAvaliacao?.id === id) {
      setCurrentAvaliacao(undefined);
    }
  };

  return (
    <>
      <div className="card mb-3 shadow-sm">
        <div className="row g-0">
          {/* Coluna da imagem + QR Code */}
          <div className="col-md-4 d-flex flex-column align-items-center p-2">
            <Link to={`/flight-details/${pacote.id}`}>
              <img
                src={imageUrl}
                alt={pacote.titulo}
                className="img-fluid rounded mb-2"
                style={{ cursor: "pointer" }}
              />
            </Link>

            {qrCodeUsuario && (
              <div className="qr-code mt-2 text-center">
                <img
                  src={`data:image/png;base64,${qrCodeUsuario}`}
                  alt="QR Code"
                  className="img-fluid"
                  style={{ maxWidth: "150px" }}
                />
                <p className="small mt-1">QR Code</p>

                <button
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={() =>
                    openModal({
                      base64: qrCodeUsuario,
                      url: `${window.location.origin}/?purchaseId=${compraId}`,
                      compraId,
                    })
                  }
                >
                  See purchase details
                </button>
              </div>
            )}
          </div>

          {/* Coluna de informações */}
          <div className="col-md-8">
            <div className={`card-body d-flex flex-column h-100 ${styles.infoCol}`}>
              <h4 className={`mb-3 ${styles.title}`}>
                <Link to={`/flight-details/${pacote.id}`} className={styles.link}>
                  {pacote.titulo}
                </Link>
              </h4>

              {/* Status + Data */}
              <div className={`${styles.section} mb-3`}>
                <span className={`${styles.status} ${styles[`status_${status.toLowerCase()}`]}`}>
                  {statusLabels[status] ?? status}
                </span>
                <span className="ms-3 text-muted">
                  {new Date(dataReserva).toLocaleDateString()}
                </span>
              </div>

              {/* Detalhes */}
              <div className={`${styles.section} mb-3`}>
                <div className={styles.detailItem}>
                  <i className="bi bi-clock me-2 text-secondary"></i>
                  <span className={styles.detailLabel}>Duration:</span>
                  <span className={styles.detailValue}>{pacote.duracaoEmMinutos} min</span>
                </div>

                <div className={styles.detailItem}>
                  <i className="bi bi-people me-2 text-secondary"></i>
                  <span className={styles.detailLabel}>Passengers:</span>
                  <span className={styles.detailValue}>
                    {adultos} adults
                    {criancas > 0 && `, ${criancas} children`}
                  </span>
                </div>

                <div className={styles.detailItem}>
                  <i className="bi bi-person-badge me-2 text-secondary"></i>
                  <span className={styles.detailLabel}>Driver:</span>
                  <span className={styles.detailValue}>{motoristaNome}</span>
                </div>
              </div>

              {/* Preço */}
              <div className={`${styles.section} d-flex justify-content-between align-items-center mb-3`}>
                <span className="fw-medium text-muted">Total</span>
                <span className={styles.total}>€ {precoTotal.toFixed(2)}</span>
              </div>

              {/* Avaliação */}
              {currentAvaliacao && (
                <div className={`${styles.reviewBox} mb-3 d-flex justify-content-between align-items-center`}>
                  <span>
                    <i className="bi bi-star-fill text-warning me-2"></i>
                    <strong>Your Review:</strong> {currentAvaliacao.nota} / 5
                  </span>

                  {/* Botão Delete */}
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => setSelectedReviewId(currentAvaliacao.id)}
                    data-bs-toggle="modal"
                    data-bs-target="#delete_modal"
                  >
                    <i className="bi bi-trash me-1"></i>
                    Delete
                  </button>
                </div>
              )}

              {/* Botões */}
              <div className="d-flex gap-2 mt-auto">
                <Link to={`/flight-details/${pacote.id}`} className="btn btn-outline-primary">
                  See Details
                </Link>

                {/* 🔹 Botão de download da fatura */}

                {["PAGO", "CONCLUIDO", "EM_ANDAMENTO"].includes(status.toUpperCase()) && (
                  <button
                    className="btn btn-outline-primary"
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${BASE_URL}/pacotes/${compraId}/fatura`, {
                          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                          responseType: "blob",
                        });

                        // Cria URL temporária para download
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const link = document.createElement("a");
                        link.href = url;
                        link.setAttribute("download", `fatura_${compraId}.pdf`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                      } catch (err) {
                        console.error("Erro ao baixar fatura:", err);
                        alert("Não foi possível baixar a fatura. Tente novamente mais tarde.");
                      }
                    }}
                  >
                    Download Invoice
                  </button>
                )}

                <Link to={`/user/chat/${compraId}`} className="btn btn-outline-primary position-relative">
                  Chat
                  {unreadCount > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                      {unreadCount}
                    </span>
                  )}
                </Link>

                {/* 🔹 ReviewButton sempre disponível para criar/editar */}
                {status.toUpperCase() === "CONCLUIDO" && (
                  <ReviewButton compraId={compraId} pacoteId={pacote.id} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🔹 Modal de Delete */}
      <DeleteReviewModal
        selectedReviewId={selectedReviewId}
        onDelete={handleDeleteReview}
        BASE_URL={BASE_URL!}
      />
    </>
  );
};

export default BookingCard;
